import { MessageCircle, ClipboardList, PackageOpen, FileText, Ship, Activity, CheckCircle2 } from "lucide-react";
import { TID } from "@/lib/testIds";

const steps = [
  { key: "consultation", Icon: MessageCircle, title: "Consultation", desc: "We understand your shipment, timeline and cargo requirements." },
  { key: "planning", Icon: ClipboardList, title: "Freight Planning", desc: "Our team designs the optimal route across air, ocean and road." },
  { key: "collection", Icon: PackageOpen, title: "Cargo Collection", desc: "We collect your shipment from your facility or supplier safely." },
  { key: "documentation", Icon: FileText, title: "Documentation & Customs", desc: "Complete import/export documentation and clearance handled in-house." },
  { key: "shipping", Icon: Ship, title: "International Transportation", desc: "FCL, LCL, charter or express — moved with full operational control." },
  { key: "tracking", Icon: Activity, title: "Tracking & Monitoring", desc: "Continuous shipment monitoring with proactive status updates." },
  { key: "delivery", Icon: CheckCircle2, title: "Final Delivery", desc: "Door-to-door handover with proof of delivery and full accountability." },
];

export default function Process() {
  return (
    <section
      id="process"
      data-testid={TID.process.section}
      className="relative bg-slate-50 border-t border-slate-200 grain"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-7 reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-600 mb-4">
              Our Process
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.02]">
              From consultation<br />
              to <span className="text-sky-500">final delivery.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end reveal">
            <p className="text-slate-600 text-lg md:text-xl">
              Seven tightly orchestrated steps. Designed to move cargo with zero drama and complete visibility.
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-slate-300 -translate-x-0 md:-translate-x-px" />
          <div className="absolute left-6 md:left-1/2 top-0 h-24 w-px bg-gradient-to-b from-sky-400 to-transparent -translate-x-0 md:-translate-x-px beam" />

          <ol className="space-y-12 md:space-y-20">
            {steps.map((s, i) => {
              const right = i % 2 === 1;
              return (
                <li
                  key={s.key}
                  data-testid={TID.process.step(s.key)}
                  className="relative reveal"
                >
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center ${right ? "md:[direction:rtl]" : ""}`}>
                    <div className={`pl-16 md:pl-0 ${right ? "md:pr-16 md:[direction:ltr]" : "md:pr-16"}`}>
                      <div className={`bg-white border border-slate-200 p-6 md:p-8 ${right ? "md:ml-0 md:mr-auto md:text-left" : "md:ml-auto"}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="font-mono text-xs text-sky-600 tracking-widest">STEP {String(i + 1).padStart(2, "0")}</span>
                          <span className="h-px flex-1 bg-slate-200" />
                        </div>
                        <h3 className="font-display font-bold text-2xl md:text-3xl text-slate-900 mb-2">{s.title}</h3>
                        <p className="text-slate-600 text-base md:text-lg leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:[direction:ltr]" />
                  </div>

                  <div className="absolute left-6 md:left-1/2 top-6 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10">
                    <div className="w-12 h-12 bg-blue-900 text-sky-400 flex items-center justify-center border-4 border-slate-50">
                      <s.Icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
